import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BotSummary } from '../../features/bots/types'
import { getQueryKey } from '../../services'
import { handleHttpErrors } from '../../shared'
import { getAccountPreference } from '../../services/preferences/userPreferences'
import { useSetAccountPreferences } from '../users'
import { showErrorToast } from '@bpinternal/ui-kit-next'
import { getApiClient } from '../../stores'
import { listBots } from '~/features/bots/services/api'

type deleteBotParams = { options: { onError?: () => Promise<void>; onSuccess?: () => Promise<void> } } & Parameters<
  ReturnType<typeof getApiClient>['deleteBot']
>['0']

export function useDeleteBot(workspaceId: string) {
  const queryClient = useQueryClient()

  const listBotsQueryKey = getQueryKey('workspaces_/$workspaceId_/bots_', { workspaceId })
  const { mutate: setPreference } = useSetAccountPreferences()

  return useMutation({
    mutationFn: async ({ id }: deleteBotParams) => {
      return await getApiClient({ workspaceId }).deleteBot({ id }).catch(handleHttpErrors)
    },
    onSuccess: async (_, { id, options: { onSuccess } }) => {
      queryClient.setQueryData<BotSummary[]>(listBotsQueryKey, (prevBots) => {
        return prevBots?.filter((bot) => bot.id !== id)
      })

      const prefsProps = { path: '$workspaceId/botHistory', params: { workspaceId } } as const
      const botHistory = await getAccountPreference(prefsProps)
      setPreference({ ...prefsProps, value: botHistory.filter((botId) => botId !== id) })

      await onSuccess?.()
    },
    onError: async (error: Error, { options: { onError } }) => {
      showErrorToast(error.message)
      await onError?.()
    },
  })
}
